import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * SectionEyebrow — small mono-uppercase tracked label preceded by a sharp
 * primary square indicator. The "Switchboard-Modernist" eyebrow used above
 * section headings and inside PlanCard / MarketingCard.
 *
 * Theme-token based: renders on the marketing dark theme and the portal light
 * theme from the SAME source. `muted` tone resolves to `--color-muted-foreground`
 * (grey in both themes); `primary` to the brand orange.
 *
 * Hoisted from marketing/src into @sighthost/ui (2026-07-08) so both apps share
 * one definition — see ARCHITECTURE_FRAMEWORK.md §3.2.
 *
 * TWO FIXES, 2026-08-30, both consequences of "shared" meaning it has to hold
 * on BOTH grounds:
 *
 * 1. The TEXT of the `primary` tone now uses `text-primary-ink`, not
 *    `text-primary`. The brand orange as text is 6.08:1 on the marketing dark
 *    ground and **2.04:1 on the portal's light ground** — a flat AA failure.
 *    This component is used in the portal exactly once, on Phase10OrderPage,
 *    for a PRICE label. `primary-ink` is the brand hue darkened until legible
 *    on light and left untouched on dark, so one class is correct in both.
 *    The square INDICATOR keeps `bg-primary`: it is a 6px decorative block
 *    beside its own label, not something anyone reads.
 *
 * 2. The size comes from `text-label`, not a hardcoded `text-[11px]`. A shared
 *    component carrying a one-off size is how a design system ends up with
 *    four label sizes — this one was the 11px in a field of 10s.
 */

export interface SectionEyebrowProps {
  children: ReactNode;
  /** Optional 1-based section index — renders as "01/" prefix. */
  index?: number;
  /** Tone — primary (default, orange) or muted (grey — for quieter sections). */
  tone?: 'primary' | 'muted';
  className?: string;
}

export function SectionEyebrow({
  children,
  index,
  tone = 'primary',
  className,
}: SectionEyebrowProps) {
  return (
    <p
      className={cn(
        'inline-flex items-center gap-2 font-mono text-label uppercase tracking-[0.3em]',
        tone === 'primary' ? 'text-primary-ink' : 'text-muted-foreground',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5',
          tone === 'primary' ? 'bg-primary' : 'bg-muted-foreground',
        )}
      />
      {index !== undefined && (
        <span className={tone === 'primary' ? 'text-primary-ink/60' : 'text-muted-foreground/70'}>
          {String(index).padStart(2, '0')}/
        </span>
      )}
      {children}
    </p>
  );
}
